import jsQR from 'jsqr';
import { createCameraSession, normalizeQrIdentifier, cameraErrorKey } from './accreditation-camera.js';

export function createAccreditationScanner(root, text) {
    const dialog = root.querySelector('[data-qr-dialog]');
    const video = dialog.querySelector('video');
    const status = dialog.querySelector('[data-qr-feedback]');
    const select = dialog.querySelector('[data-qr-camera]');
    const cameraField = dialog.querySelector('[data-qr-camera-field]');
    const retry = dialog.querySelector('[data-qr-retry]');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    let owner = null;
    let receive = null;
    let timer = null;
    let run = 0;

    const camera = createCameraSession(video, navigator.mediaDevices, () => fail('scanner.interrupted'));

    function feedback(key, error = false) {
        status.textContent = text(key);
        status.classList.toggle('is-error', error);
    }

    function stop() {
        run++;
        clearTimeout(timer);
        camera.stop();
    }

    function close() {
        stop();
        const trigger = owner && owner.querySelector('[data-scan]');
        owner = null;
        receive = null;

        if (dialog.open) {
            dialog.close();
        }
        if (trigger && trigger.isConnected && !trigger.disabled) {
            trigger.focus();
        }
    }

    function fail(key) {
        stop();
        feedback(key, true);
        retry.hidden = false;
        select.disabled = false;
    }

    async function listCameras(current) {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            if (current !== run || !dialog.open) {
                return;
            }

            const cameras = devices.filter(device => device.kind === 'videoinput' && device.deviceId);
            const active = video.srcObject?.getVideoTracks()[0]?.getSettings().deviceId;
            select.replaceChildren();
            cameras.forEach((device, index) => {
                select.add(new Option(device.label || text('scanner.camera_number', { number: index + 1 }), device.deviceId));
            });
            if (active) {
                select.value = active;
            }
            cameraField.hidden = cameras.length < 2;
        } catch (error) {
            // An unavailable device list must not interrupt an already running camera.
            if (current === run) {
                cameraField.hidden = true;
            }
        }
    }

    function scan(current) {
        if (current !== run || !dialog.open) {
            return;
        }

        if (!owner?.isConnected || owner.elements.qrIdentifier.disabled) {
            close();
            return;
        }

        try {
            if (video.readyState >= 2 && video.videoWidth && video.videoHeight) {
                const scale = Math.min(1, 960 / Math.max(video.videoWidth, video.videoHeight));
                canvas.width = Math.round(video.videoWidth * scale);
                canvas.height = Math.round(video.videoHeight * scale);
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
                const result = jsQR(pixels.data, pixels.width, pixels.height, { inversionAttempts: 'attemptBoth' });

                if (result) {
                    const identifier = normalizeQrIdentifier(result.data);

                    if (identifier) {
                        const callback = receive;
                        close();
                        callback(identifier);
                        return;
                    }

                    feedback('scanner.invalid', true);
                }
            }
        } catch (error) {
            fail('scanner.failed');
            return;
        }

        timer = setTimeout(() => scan(current), 150);
    }

    async function start(deviceId = '') {
        stop();
        const current = run;
        retry.hidden = true;
        select.disabled = true;
        feedback('scanner.starting');

        try {
            if (!await camera.start(deviceId) || current !== run || !dialog.open) {
                return;
            }

            select.disabled = false;
            feedback('scanner.aim');
            listCameras(current);
            scan(current);
        } catch (error) {
            if (current === run && dialog.open) {
                fail(cameraErrorKey(error));
            }
        }
    }

    function open(form, callback) {
        close();
        owner = form;
        receive = callback;
        cameraField.hidden = true;
        retry.hidden = true;

        if (typeof dialog.showModal !== 'function') {
            close();
            return false;
        }

        dialog.showModal();

        if (!window.isSecureContext) {
            feedback('scanner.insecure', true);
        } else if (!navigator.mediaDevices?.getUserMedia || !context) {
            feedback('scanner.unsupported', true);
        } else {
            start();
        }

        return true;
    }

    dialog.querySelector('[data-qr-close]').addEventListener('click', close);
    dialog.addEventListener('cancel', event => {
        event.preventDefault();
        close();
    });
    select.addEventListener('change', () => start(select.value));
    retry.addEventListener('click', () => start());
    window.addEventListener('pagehide', close);
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            close();
        }
    });

    root.addEventListener('accreditation:scan', event => {
        if (event.defaultPrevented || typeof event.detail?.receive !== 'function') {
            return;
        }

        const form = event.target.closest('[data-ticket-form]');
        if (!form || form.elements.qrIdentifier.disabled) {
            return;
        }

        if (open(form, event.detail.receive)) {
            event.preventDefault();
        }
    });

    return { close, isOpen: () => dialog.open };
}
